/* eslint-disable no-restricted-globals, react/sort-comp */
import { isEmpty, uniqueId, compose } from 'underscore';
import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { usePopupContext, withPopupProvider, withPopupContext } from './popup-context';
import { priorityLevel } from './const';
import type {
  IPopupManager, IUseLockOptions, IWithLockOptions, IWithLockProps,
} from './types';

/* istanbul ignore next */
export const useLock = ({
  key,
  priority = priorityLevel.default,
  force = false,
  isReady = true,
  autoClose = false,
}: IUseLockOptions) => {
  const popupManager = usePopupContext();

  const lockKey = useMemo(() => {
    if (isEmpty(key)) {
      return uniqueId('popup-');
    }
    return uniqueId(`${key}-`);
  }, [key]);

  const [lock, setLock] = useState<boolean>(false);

  const acquireLock = useCallback(async () => {
    if (force) {
      setLock((popupManager as IPopupManager).forceAcquireLock(lockKey));
    } else {
      setLock(await (popupManager as IPopupManager).acquireLock({
        key: lockKey,
        priority,
      }));
    }
  }, [popupManager, lockKey, priority, force]);

  useEffect(() => {
    if (isReady) {
      acquireLock();
    }
  }, [isReady, acquireLock]);

  const releaseLock = useCallback(() => {
    (popupManager as IPopupManager).releaseLock(lockKey);
    setLock(false);
  }, [popupManager, lockKey]);

  useEffect(() => {
    const oldPushState = history.pushState;
    history.pushState = function pushState() /* istanbul ignore next */ {
      /* istanbul ignore next */
      if (lock && autoClose) {
        releaseLock();
      }
      const ret = oldPushState.apply(this, arguments as any); // eslint-disable-line prefer-rest-params
      return ret;
    };
  }, [lock, releaseLock, autoClose]);

  return {
    lock,
    releaseLock,
  };
};

export const withLock = (WrappedComponent: React.ComponentType<any>, {
  lockKey,
  priority,
  force,
  wait,
  autoClose,
}: IWithLockOptions = {}) => {
  class ComponentWithLock extends React.PureComponent<IWithLockProps, { lock: boolean }> {
    autoClose: boolean;

    force: boolean;

    lockKey: string;

    popupManager: IPopupManager;

    priority: number | undefined;

    wait: boolean | undefined;

    static defaultProps: Partial<IWithLockProps> = {
      lockKey: '',
      priority: null,
      force: false,
      wait: false,
      autoClose: false,
    };

    constructor(props: IWithLockProps) {
      super(props);

      this.popupManager = props.popupManager;
      this.lockKey = props.lockKey || lockKey;
      this.priority = props.priority || priority;
      this.force = props.force || force;
      this.wait = props.wait || wait;
      this.autoClose = props.autoClose || autoClose;

      if (isEmpty(this.lockKey)) {
        this.lockKey = uniqueId('popup-');
      } else {
        this.lockKey = uniqueId(`${this.lockKey}-`);
      }

      this.state = {
        lock: false,
      };
    }

    componentDidMount() {
      this.acquireLock();

      const handleLocationChange = () => /* istanbul ignore next */ {
        if (this.state.lock && this.autoClose) {
          this.popupManager.releaseLock(this.lockKey);
          this.setState({
            lock: false,
          });
        }
      };

      const oldPushState = history.pushState;
      history.pushState = function pushState() /* istanbul ignore next */ {
        handleLocationChange();
        const ret = oldPushState.apply(this, arguments as any); // eslint-disable-line prefer-rest-params
        return ret;
      };
    }

    componentWillUnmount() {
      this.popupManager.releaseLock(this.lockKey);
    }

    async acquireLock() {
      let lock: boolean;
      if (this.force) {
        lock = this.popupManager.forceAcquireLock(this.lockKey);
      } else {
        lock = await this.popupManager.acquireLock({
          key: this.lockKey,
          priority: this.priority as number,
          wait: this.wait,
        });
      }
      this.setState({
        lock,
      });
    }

    render() {
      return this.state.lock && (
        <WrappedComponent
          {...this.props}
        />
      );
    }
  }

  return compose(
    withPopupProvider,
    withPopupContext
  )(ComponentWithLock);
};
